import { useState, useEffect } from 'react';
import { 
  TextInput, 
  Textarea, 
  Button, 
  Group, 
  Select, 
  Text, 
  Stack, 
  Box, 
  Avatar, 
  MultiSelect,
  Card,
  Title,
  Divider
} from '@mantine/core';
import { useForm } from '@mantine/form';
import { mockTeamMembers, Team } from '../../types/team';

interface TeamFormProps {
  team?: Team;
  onSubmit: (values: Omit<Team, 'id' | 'createdAt' | 'updatedAt'>) => void;
  onCancel: () => void;
}

interface TeamFormValues {
  name: string;
  leaderId: string;
  members: string[];
}

export function TeamForm({ team, onSubmit, onCancel }: TeamFormProps) {
  // Transform members data for MultiSelect
  const memberOptions = mockTeamMembers.map(member => ({
    value: member.id,
    label: member.name,
    description: member.role,
    avatar: member.avatar
  }));

  // Initialize form with existing team data or defaults
  const form = useForm<TeamFormValues>({
    initialValues: {
      name: team?.name || '',
      leaderId: team?.leaderId || '',
      members: team?.members || []
    },
    validate: {
      name: (value) => (value.trim().length === 0 ? 'Team name is required' : null),
      leaderId: (value) => (value.trim().length === 0 ? 'Team leader is required' : null),
      members: (value) => (value.length === 0 ? 'At least one team member is required' : null)
    }
  });

  // Update members when leader changes (leader should always be a member)
  useEffect(() => {
    const leaderId = form.values.leaderId;
    if (leaderId && !form.values.members.includes(leaderId)) {
      form.setFieldValue('members', [...form.values.members, leaderId]);
    }
  }, [form.values.leaderId]);

  // Handle form submission
  const handleSubmit = form.onSubmit((values) => {
    onSubmit(values);
  });

  // Render select items with avatars
  const renderSelectItem = ({ value, label, description, avatar }: { 
    value: string; 
    label: string; 
    description: string;
    avatar?: string;
  }) => (
    <Group gap="sm">
      <Avatar src={avatar} color="brand" size="sm" radius="xl">
        {label.substring(0, 2)}
      </Avatar>
      <Box>
        <Text size="sm">{label}</Text>
        <Text size="xs" c="dimmed">
          {description}
        </Text>
      </Box>
    </Group>
  );

  return (
    <Card shadow="sm" padding="lg" radius="md" withBorder>
      <Group justify="space-between" mb="md">
        <Title order={2} size="h3">{team ? 'Edit Team' : 'Create New Team'}</Title>
      </Group>
      
      <form onSubmit={handleSubmit}>
        <Stack gap="md">
          <TextInput
            label="Team Name"
            placeholder="Enter team name"
            required
            {...form.getInputProps('name')}
          />

          <Divider label="Team Leadership & Members" labelPosition="center" />
          
          <Select
            label="Team Leader"
            placeholder="Select team leader"
            data={memberOptions}
            required
            searchable
            maxDropdownHeight={200}
            nothingFoundMessage="No such person"
            {...form.getInputProps('leaderId')}
          />
          
          <MultiSelect
            label="Team Members"
            placeholder="Select team members"
            data={memberOptions}
            required
            searchable
            maxDropdownHeight={200}
            nothingFoundMessage="No team members found"
            {...form.getInputProps('members')}
          />
          
          <Group justify="flex-end" mt="md">
            <Button variant="outline" onClick={onCancel}>Cancel</Button>
            <Button type="submit">{team ? 'Update Team' : 'Create Team'}</Button>
          </Group>
        </Stack>
      </form>
    </Card>
  );
} 
